/**
 * usePlatformARDemoLaunchLogLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait PlatformARDemoLaunchLog
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, PlatformARDemoLaunchEntry, PlatformARDemoLaunchLogEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type PlatformARDemoLaunchLogState =
  | 'loading'
  | 'viewing'
  | 'backfilling'
  | 'error';

export type PlatformARDemoLaunchLogEvent =
  | 'INIT'
  | 'EventLogLoaded'
  | 'EventLogLoadFailed'
  | 'EventLogSaved'
  | 'EventLogSaveFailed'
  | 'APPLY_FILTER'
  | 'REFETCH_QUERY'
  | 'REFETCH_FILTER'
  | 'REFETCH_PAGE'
  | 'OPEN_BACKFILL'
  | 'SAVE_BACKFILL'
  | 'CANCEL_BACKFILL'
  | 'ENTRIES_CHANGED';

type PlatformARDemoLaunchLogSlot = 'main' | 'toast';
interface PlatformARDemoLaunchLogInternalState {
  machineState: PlatformARDemoLaunchLogState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: PlatformARDemoLaunchLogEvent | null;
  slotWriter: Partial<Record<PlatformARDemoLaunchLogSlot, PlatformARDemoLaunchLogEvent | null>>;
  loading: boolean;
  pendingEvent: PlatformARDemoLaunchLogEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<PlatformARDemoLaunchEntry>;
}

type PlatformARDemoLaunchLogAction =
  | { type: 'EVENT_SUCCESS'; event: PlatformARDemoLaunchLogEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: PlatformARDemoLaunchLogSlot[]; renderClear?: PlatformARDemoLaunchLogSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: PlatformARDemoLaunchLogEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof PlatformARDemoLaunchEntry & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function platformARDemoLaunchLogReducer(
  state: PlatformARDemoLaunchLogInternalState,
  action: PlatformARDemoLaunchLogAction
): PlatformARDemoLaunchLogInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as PlatformARDemoLaunchLogState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<PlatformARDemoLaunchEntry>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<PlatformARDemoLaunchEntry>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<PlatformARDemoLaunchEntry>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: PlatformARDemoLaunchLogInternalState = {
  machineState: 'loading',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface PlatformARDemoLaunchLogConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  allLabel?: string;
  bodySearch: boolean;
  cardLook: 'elevated' | 'flat-bordered' | 'borderless-divider' | 'ticket' | 'invoice' | 'chip' | 'tile-image-first';
  dateField: string;
  descriptionField: string;
  filterBarLook: 'toolbar' | 'chips' | 'pills' | 'popover-trigger' | 'inline-column-header';
  filters: { field: string; label?: string; options?: string[] }[];
  formFields: string[];
  kindField: string;
  kindLabels?: string[];
  kindOptions: { icon: string; key: string; label: string; status: string }[];
  pageSize: number;
  searchField: string;
  searchPlaceholder: string;
  statusField: string;
  timelineLook: 'vertical-compact' | 'vertical-spacious' | 'horizontal' | 'swimlane';
  title?: string;
  titleField: string;
}

const DEFAULT_PLATFORM_AR_DEMO_LAUNCH_LOG_CONFIG = {
  bodySearch: false,
  cardLook: "elevated",
  dateField: "date",
  descriptionField: "description",
  filterBarLook: "toolbar",
  filters: [],
  formFields: ["backfillTitle", "backfillDescription", "backfillKind", "backfillDate"],
  kindField: "kind",
  kindOptions: [{ icon: "edit-3", key: "described", label: "Described", status: "active" }, { icon: "check-circle", key: "verified", label: "Verified", status: "complete" }, { icon: "globe", key: "hosted", label: "Live", status: "complete" }],
  pageSize: 20,
  searchField: "title",
  searchPlaceholder: "Search activity…",
  statusField: "status",
  timelineLook: "vertical-spacious",
  titleField: "title",
} as PlatformARDemoLaunchLogConfig;

export interface PlatformARDemoLaunchLogLogicReturn {
  state: PlatformARDemoLaunchLogState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: PlatformARDemoLaunchLogEvent | null;
  slotWriter: Partial<Record<PlatformARDemoLaunchLogSlot, PlatformARDemoLaunchLogEvent | null>>;
  loading: boolean;
  pendingEvent: PlatformARDemoLaunchLogEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<PlatformARDemoLaunchEntry>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof PlatformARDemoLaunchLogEventPayloadMap]: (payload?: PlatformARDemoLaunchLogEventPayloadMap[K]) => Promise<EventResponse> };
  config: PlatformARDemoLaunchLogConfig;
}

const __CASCADE_EVENTS = new Set<string>(['ENTRIES_CHANGED', 'REFETCH_FILTER']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['EventLogSaved', 'INIT', 'REFETCH_FILTER', 'REFETCH_PAGE', 'REFETCH_QUERY', 'SAVE_BACKFILL']);

export function usePlatformARDemoLaunchLogLogic(config?: Partial<PlatformARDemoLaunchLogConfig>): PlatformARDemoLaunchLogLogicReturn {
  const mergedConfig: PlatformARDemoLaunchLogConfig = { ...DEFAULT_PLATFORM_AR_DEMO_LAUNCH_LOG_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/platform-ar-demo-launch-log', orbitalName: 'PlatformAR', traitName: 'PlatformARDemoLaunchLog' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(platformARDemoLaunchLogReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['PlatformARDemoLaunchEntry'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'loading' && event === 'INIT') {
      // loading → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'EventLogLoaded') {
      // loading → viewing (on EventLogLoaded)
        _fields['filterChips'] = [{ label: "All" as const, icon: "list" as const, id: "" as const }, ...(([{ key: "described" as const, status: "active" as const, label: "Described" as const, icon: "edit-3" as const }, { key: "verified" as const, icon: "check-circle" as const, status: "complete" as const, label: "Verified" as const }, { key: "hosted" as const, icon: "globe" as const, status: "complete" as const, label: "Live" as const }]).map((k) => ({ icon: (getNestedValue(k, "icon") ?? "circle"), label: getNestedValue(k, "label"), id: getNestedValue(k, "key") })))];
        fields = { ...(fields ?? {}), 'filterChips': _fields['filterChips'] } as Record<string, EventPayloadValue | undefined>;
        _fields['allEntries'] = [...(((payloadObj?.data ?? []) as readonly EntityRow[]).map((row) => ({ id: (getNestedValue(row, "id") ?? ""), status: (getNestedValue((([{ icon: "edit-3" as const, status: "active" as const, label: "Described" as const, key: "described" as const }, { key: "verified" as const, status: "complete" as const, label: "Verified" as const, icon: "check-circle" as const }, { icon: "globe" as const, label: "Live" as const, key: "hosted" as const, status: "complete" as const }]).find((k) => (getNestedValue(k, "key") === (getNestedValue(row, "kind") ?? ""))) || {  }), "status") ?? "pending"), date: (getNestedValue(row, "createdAt") ?? (getNestedValue(row, "date") ?? "")), description: (getNestedValue(row, "description") ?? ""), title: (getNestedValue(row, "title") ?? ""), kind: (getNestedValue(row, "kind") ?? "") })))].sort((a, b) => { const av = (a as EntityRow)?.["date"], bv = (b as EntityRow)?.["date"]; const dir = (("desc") as string) === 'desc' ? -1 : 1; if ((av as number) < (bv as number)) return -1 * dir; if ((av as number) > (bv as number)) return 1 * dir; return 0; });
        fields = { ...(fields ?? {}), 'allEntries': _fields['allEntries'] } as Record<string, EventPayloadValue | undefined>;
        _fields['entries'] = [...(((payloadObj?.data ?? []) as readonly EntityRow[]).map((row) => ({ description: (getNestedValue(row, "description") ?? ""), id: (getNestedValue(row, "id") ?? ""), status: (getNestedValue((([{ label: "Described" as const, key: "described" as const, status: "active" as const, icon: "edit-3" as const }, { icon: "check-circle" as const, key: "verified" as const, label: "Verified" as const, status: "complete" as const }, { icon: "globe" as const, key: "hosted" as const, label: "Live" as const, status: "complete" as const }]).find((k) => (getNestedValue(k, "key") === (getNestedValue(row, "kind") ?? ""))) || {  }), "status") ?? "pending"), date: (getNestedValue(row, "createdAt") ?? (getNestedValue(row, "date") ?? "")), kind: (getNestedValue(row, "kind") ?? ""), title: (getNestedValue(row, "title") ?? "") })))].sort((a, b) => { const av = (a as EntityRow)?.["date"], bv = (b as EntityRow)?.["date"]; const dir = (("desc") as string) === 'desc' ? -1 : 1; if ((av as number) < (bv as number)) return -1 * dir; if ((av as number) > (bv as number)) return 1 * dir; return 0; });
        fields = { ...(fields ?? {}), 'entries': _fields['entries'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing';
    } else if (fromState === 'loading' && event === 'EventLogLoadFailed') {
      // loading → error (on EventLogLoadFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'error';
    } else if (fromState === 'loading' && event === 'EventLogSaved') {
      // loading → loading (on EventLogSaved)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'EventLogSaveFailed') {
      // loading → error (on EventLogSaveFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'error';
    } else if (fromState === 'viewing' && event === 'INIT') {
      // viewing → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'viewing' && event === 'APPLY_FILTER') {
      // viewing → viewing (on APPLY_FILTER)
        _fields['filterKind'] = payloadObj?.tabId;
        fields = { ...(fields ?? {}), 'filterKind': _fields['filterKind'] } as Record<string, EventPayloadValue | undefined>;
        _fields['entries'] = ((fields?.allEntries ?? []) as readonly { date?: string; description?: string; id: string; kind?: 'created' | 'updated' | 'approved' | 'rejected'; status?: 'complete' | 'active' | 'pending' | 'error'; title: string }[]).filter((e) => ((payloadObj?.tabId === "") || (getNestedValue(e, "kind") === payloadObj?.tabId)));
        fields = { ...(fields ?? {}), 'entries': _fields['entries'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'REFETCH_QUERY') {
      // viewing → viewing (on REFETCH_QUERY)
      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'REFETCH_FILTER') {
      // viewing → viewing (on REFETCH_FILTER)
      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'REFETCH_PAGE') {
      // viewing → viewing (on REFETCH_PAGE)
      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'EventLogLoadFailed') {
      // viewing → viewing (on EventLogLoadFailed)
      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'EventLogLoaded') {
      // viewing → viewing (on EventLogLoaded)
        _fields['filterChips'] = [{ id: "" as const, label: "All" as const, icon: "list" as const }, ...(([{ icon: "edit-3" as const, label: "Described" as const, status: "active" as const, key: "described" as const }, { key: "verified" as const, icon: "check-circle" as const, status: "complete" as const, label: "Verified" as const }, { key: "hosted" as const, status: "complete" as const, label: "Live" as const, icon: "globe" as const }]).map((k) => ({ label: getNestedValue(k, "label"), icon: (getNestedValue(k, "icon") ?? "circle"), id: getNestedValue(k, "key") })))];
        fields = { ...(fields ?? {}), 'filterChips': _fields['filterChips'] } as Record<string, EventPayloadValue | undefined>;
        _fields['allEntries'] = [...(((payloadObj?.data ?? []) as readonly EntityRow[]).map((row) => ({ id: (getNestedValue(row, "id") ?? ""), status: (getNestedValue((([{ icon: "edit-3" as const, label: "Described" as const, status: "active" as const, key: "described" as const }, { key: "verified" as const, label: "Verified" as const, status: "complete" as const, icon: "check-circle" as const }, { label: "Live" as const, icon: "globe" as const, key: "hosted" as const, status: "complete" as const }]).find((k) => (getNestedValue(k, "key") === (getNestedValue(row, "kind") ?? ""))) || {  }), "status") ?? "pending"), kind: (getNestedValue(row, "kind") ?? ""), title: (getNestedValue(row, "title") ?? ""), date: (getNestedValue(row, "createdAt") ?? (getNestedValue(row, "date") ?? "")), description: (getNestedValue(row, "description") ?? "") })))].sort((a, b) => { const av = (a as EntityRow)?.["date"], bv = (b as EntityRow)?.["date"]; const dir = (("desc") as string) === 'desc' ? -1 : 1; if ((av as number) < (bv as number)) return -1 * dir; if ((av as number) > (bv as number)) return 1 * dir; return 0; });
        fields = { ...(fields ?? {}), 'allEntries': _fields['allEntries'] } as Record<string, EventPayloadValue | undefined>;
        _fields['entries'] = [...(((payloadObj?.data ?? []) as readonly EntityRow[]).map((row) => ({ date: (getNestedValue(row, "createdAt") ?? (getNestedValue(row, "date") ?? "")), title: (getNestedValue(row, "title") ?? ""), id: (getNestedValue(row, "id") ?? ""), status: (getNestedValue((([{ label: "Described" as const, status: "active" as const, icon: "edit-3" as const, key: "described" as const }, { status: "complete" as const, icon: "check-circle" as const, label: "Verified" as const, key: "verified" as const }, { key: "hosted" as const, status: "complete" as const, icon: "globe" as const, label: "Live" as const }]).find((k) => (getNestedValue(k, "key") === (getNestedValue(row, "kind") ?? ""))) || {  }), "status") ?? "pending"), description: (getNestedValue(row, "description") ?? ""), kind: (getNestedValue(row, "kind") ?? "") })))].sort((a, b) => { const av = (a as EntityRow)?.["date"], bv = (b as EntityRow)?.["date"]; const dir = (("desc") as string) === 'desc' ? -1 : 1; if ((av as number) < (bv as number)) return -1 * dir; if ((av as number) > (bv as number)) return 1 * dir; return 0; });
        fields = { ...(fields ?? {}), 'entries': _fields['entries'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'OPEN_BACKFILL') {
      // viewing → backfilling (on OPEN_BACKFILL)
        _fields['backfillTitle'] = "";
        fields = { ...(fields ?? {}), 'backfillTitle': _fields['backfillTitle'] } as Record<string, EventPayloadValue | undefined>;
        _fields['backfillDescription'] = "";
        fields = { ...(fields ?? {}), 'backfillDescription': _fields['backfillDescription'] } as Record<string, EventPayloadValue | undefined>;
        _fields['backfillDate'] = "";
        fields = { ...(fields ?? {}), 'backfillDate': _fields['backfillDate'] } as Record<string, EventPayloadValue | undefined>;
        _fields['backfillKind'] = "";
        fields = { ...(fields ?? {}), 'backfillKind': _fields['backfillKind'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'backfilling';
    } else if (fromState === 'backfilling' && event === 'INIT') {
      // backfilling → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'backfilling' && event === 'SAVE_BACKFILL') {
      // backfilling → loading (on SAVE_BACKFILL)
      _newState = 'loading';
    } else if (fromState === 'backfilling' && event === 'CANCEL_BACKFILL') {
      // backfilling → viewing (on CANCEL_BACKFILL)
      _newState = 'viewing';
    } else if (fromState === 'error' && event === 'INIT') {
      // error → loading (on INIT)
      _newState = 'loading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: PlatformARDemoLaunchLogSlot[]; clear: PlatformARDemoLaunchLogSlot[] } => {
    switch (fromState) {
      case 'backfilling':
        switch (event) {
          case 'CANCEL_BACKFILL': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'INIT': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'error':
        switch (event) {
          case 'INIT': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'loading':
        switch (event) {
          case 'EventLogLoadFailed': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'EventLogLoaded': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'EventLogSaveFailed': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'EventLogSaved': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'INIT': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'viewing':
        switch (event) {
          case 'APPLY_FILTER': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'EventLogLoadFailed': return { write: ['toast'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'EventLogLoaded': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          case 'OPEN_BACKFILL': return { write: ['main'] as PlatformARDemoLaunchLogSlot[], clear: [] as PlatformARDemoLaunchLogSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'PlatformARDemoLaunchEntry': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as PlatformARDemoLaunchLogEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as PlatformARDemoLaunchLogEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as PlatformARDemoLaunchLogEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['PlatformARDemoLaunchLog'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'PlatformARDemoLaunchLog': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof PlatformARDemoLaunchLogEventPayloadMap]: (payload?: PlatformARDemoLaunchLogEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'EventLogLoaded': (payload?: PlatformARDemoLaunchLogEventPayloadMap['EventLogLoaded']) => dispatch('EventLogLoaded', payload),
    'EventLogLoadFailed': (payload?: PlatformARDemoLaunchLogEventPayloadMap['EventLogLoadFailed']) => dispatch('EventLogLoadFailed', payload),
    'EventLogSaved': (payload?: PlatformARDemoLaunchLogEventPayloadMap['EventLogSaved']) => dispatch('EventLogSaved', payload),
    'EventLogSaveFailed': (payload?: PlatformARDemoLaunchLogEventPayloadMap['EventLogSaveFailed']) => dispatch('EventLogSaveFailed', payload),
    'APPLY_FILTER': (payload?: PlatformARDemoLaunchLogEventPayloadMap['APPLY_FILTER']) => dispatch('APPLY_FILTER', payload),
    'REFETCH_QUERY': (payload?: PlatformARDemoLaunchLogEventPayloadMap['REFETCH_QUERY']) => dispatch('REFETCH_QUERY', payload),
    'REFETCH_FILTER': (payload?: PlatformARDemoLaunchLogEventPayloadMap['REFETCH_FILTER']) => dispatch('REFETCH_FILTER', payload),
    'REFETCH_PAGE': (payload?: PlatformARDemoLaunchLogEventPayloadMap['REFETCH_PAGE']) => dispatch('REFETCH_PAGE', payload),
    'OPEN_BACKFILL': () => dispatch('OPEN_BACKFILL'),
    'SAVE_BACKFILL': (payload?: PlatformARDemoLaunchLogEventPayloadMap['SAVE_BACKFILL']) => dispatch('SAVE_BACKFILL', payload),
    'CANCEL_BACKFILL': () => dispatch('CANCEL_BACKFILL'),
  };

  useUIEvents(enqueueEvent, 'PlatformAR.PlatformARDemoLaunchLog', ['INIT', 'EventLogLoaded', 'EventLogLoadFailed', 'EventLogSaved', 'EventLogSaveFailed', 'APPLY_FILTER', 'REFETCH_QUERY', 'REFETCH_FILTER', 'REFETCH_PAGE', 'OPEN_BACKFILL', 'SAVE_BACKFILL', 'CANCEL_BACKFILL'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('PlatformARDemoLaunchLog', (): TraitStateSnapshot => ({
      traitName: 'PlatformARDemoLaunchLog',
      currentState: internalStateRef.current.machineState,
      states: ['loading', 'viewing', 'backfilling', 'error'],
      events: ['INIT', 'EventLogLoaded', 'EventLogLoadFailed', 'EventLogSaved', 'EventLogSaveFailed', 'APPLY_FILTER', 'REFETCH_QUERY', 'REFETCH_FILTER', 'REFETCH_PAGE', 'OPEN_BACKFILL', 'SAVE_BACKFILL', 'CANCEL_BACKFILL'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'ENTRIES_CHANGED' (from PlatformARDemoLaunchDesk) → trigger 'REFETCH_FILTER'
    const unsub_entries_changed_from_platformardemolaunchdesk_to_refetch_filter = eventBus.on('UI:PlatformAR.PlatformARDemoLaunchDesk.ENTRIES_CHANGED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'PlatformAR.PlatformARDemoLaunchLog', 'REFETCH_FILTER')) return;
      enqueueEvent('REFETCH_FILTER', { entity: "DemoLaunchEntry", field: "lane", value: "shown" } as EventPayload);
    });
    return () => {
      if (typeof unsub_entries_changed_from_platformardemolaunchdesk_to_refetch_filter === 'function') unsub_entries_changed_from_platformardemolaunchdesk_to_refetch_filter();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  mergedConfig.allLabel = mergedConfig.allLabel ?? (__t("std-almadar-demo-launch:kind.all"));
  mergedConfig.kindLabels = mergedConfig.kindLabels ?? ([__t("std-almadar-demo-launch:kind.described"), __t("std-almadar-demo-launch:kind.verified"), __t("std-almadar-demo-launch:kind.hosted")]);
  mergedConfig.title = mergedConfig.title ?? (__t("std-almadar-demo-launch:log.title"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
